import { useState } from 'react';
import { validarCPF, formatCpf, formatCep } from './utils';

const FONT = "'DM Sans', sans-serif";

type Props = {
  slug: string;
  selectionString?: string;
  plan?: 'musica' | 'video';
  nomeSugerido?: string;
  cupomCodigo?: string | null;
};

type Endereco = {
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
};

const ENDERECO_VAZIO: Endereco = { cep: '', rua: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' };

type ResultadoBoleto = {
  linha_digitavel: string | null;
  boleto_url: string | null;
};

export default function BoletoTab({ slug, selectionString = '', plan, cupomCodigo }: Props) {
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [endereco, setEndereco] = useState<Endereco>(ENDERECO_VAZIO);
  const [cepLoading, setCepLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoBoleto | null>(null);
  const [copiado, setCopiado] = useState(false);

  const handleCepChange = async (raw: string) => {
    const formatado = formatCep(raw);
    setEndereco((e) => ({ ...e, cep: formatado }));
    const digits = formatado.replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await r.json();
      if (data.erro) throw new Error('CEP não encontrado');
      setEndereco((e) => ({
        ...e,
        rua: data.logradouro || e.rua,
        bairro: data.bairro || e.bairro,
        cidade: data.localidade || e.cidade,
        uf: data.uf || e.uf,
      }));
    } catch {
      // silencioso — usuário pode preencher manualmente
    } finally {
      setCepLoading(false);
    }
  };

  const validar = () => {
    const errs: Record<string, string> = {};
    if (!nome.trim()) errs.nome = 'Nome obrigatório';
    if (!validarCPF(cpf)) errs.cpf = 'CPF inválido';
    if (endereco.cep.replace(/\D/g, '').length !== 8) errs.cep = 'CEP inválido';
    if (!endereco.numero.trim()) errs.numero = 'Número obrigatório';
    return errs;
  };

  const errors = validar();
  const formValido = Object.keys(errors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValido || submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const r = await fetch('/api/checkout?action=boleto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          previas_selecionadas: selectionString || undefined,
          plano: plan,
          cupom_codigo: cupomCodigo || '',
          payer: {
            nome,
            cpf,
            email,
            cep: endereco.cep,
            numero: endereco.numero,
          },
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        setServerError(data.error || 'Não foi possível gerar o boleto.');
        return;
      }
      setResultado({
        linha_digitavel: data.linha_digitavel,
        boleto_url: data.boleto_url,
      });
    } catch {
      setServerError('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopiarLinha = async () => {
    if (!resultado?.linha_digitavel) return;
    try {
      await navigator.clipboard.writeText(resultado.linha_digitavel);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2400);
    } catch {
      /* noop */
    }
  };

  if (resultado) {
    return (
      <div style={{ fontFamily: FONT, animation: 'inspiraFadeIn 0.4s' }}>
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 14,
            background: '#E8F4EC',
            color: '#1f6e44',
            fontSize: 14,
            lineHeight: 1.5,
            marginBottom: 16,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <span aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>✅</span>
          <span>
            <strong>Boleto gerado!</strong> Pague e a música começa a ser produzida após a compensação (1 a 2 dias úteis).
          </span>
        </div>

        {resultado.linha_digitavel && (
          <div style={{ marginBottom: 14 }}>
            <label
              style={{
                fontSize: 11.5,
                color: 'var(--text-muted)',
                fontWeight: 600,
                marginBottom: 6,
                display: 'block',
                letterSpacing: '0.02em',
              }}
            >
              Linha digitável
            </label>
            <div
              style={{
                padding: 12,
                borderRadius: 12,
                background: 'var(--section-alt)',
                border: '1px solid var(--border-soft)',
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontSize: 12.5,
                color: 'var(--text-body)',
                wordBreak: 'break-all',
                lineHeight: 1.5,
              }}
            >
              {resultado.linha_digitavel}
            </div>
            <button
              type="button"
              onClick={handleCopiarLinha}
              style={{
                marginTop: 8,
                width: '100%',
                padding: 13,
                borderRadius: 999,
                background: copiado ? '#1f8a5b' : 'var(--gold)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                border: 'none',
                cursor: 'pointer',
                fontFamily: FONT,
                boxShadow: copiado ? '0 6px 20px rgba(31,138,91,0.32)' : '0 6px 22px rgba(255,145,77,0.36)',
                transition: 'background 0.22s, box-shadow 0.22s',
              }}
            >
              {copiado ? '✅ Copiado' : 'Copiar linha digitável'}
            </button>
          </div>
        )}

        {resultado.boleto_url && (
          <a
            href={resultado.boleto_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block',
              textAlign: 'center',
              padding: 13,
              borderRadius: 999,
              background: 'transparent',
              color: 'var(--text-strong)',
              border: '1.5px solid var(--border-soft)',
              fontWeight: 700,
              fontSize: 14,
              textDecoration: 'none',
              fontFamily: FONT,
              marginBottom: 8,
            }}
          >
            Abrir boleto em nova aba
          </a>
        )}

        <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5, marginTop: 8 }}>
          Você também pode pagar via PIX no boleto, escaneando o QR Code do PDF.
        </p>

        <style>{`@keyframes inspiraFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ fontFamily: FONT, animation: 'inspiraFadeIn 0.4s' }}>
      <div
        style={{
          padding: '12px 14px',
          borderRadius: 12,
          background: 'var(--gold-pale)',
          color: 'var(--amber-deep)',
          fontSize: 12.5,
          lineHeight: 1.55,
          marginBottom: 16,
          fontWeight: 500,
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
        }}
      >
        <span aria-hidden style={{ flexShrink: 0, marginTop: 1 }}>⏳</span>
        <span>
          <strong>Atenção:</strong> a música começa a ser produzida só após a compensação (1 a 2 dias úteis).
          Pra entrega imediata, escolha PIX ou Cartão.
        </span>
      </div>

      <Field label="Nome completo (como no documento)" error={errors.nome}>
        <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Maria Silva" style={inputStyle} />
      </Field>

      <Field label="CPF" error={errors.cpf}>
        <input
          type="text"
          inputMode="numeric"
          value={cpf}
          onChange={(e) => setCpf(formatCpf(e.target.value))}
          placeholder="000.000.000-00"
          maxLength={14}
          style={inputStyle}
        />
      </Field>

      <Field label="E-mail (pra enviar o boleto — opcional)">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" style={inputStyle} />
      </Field>

      <Field label={`CEP${cepLoading ? ' (buscando…)' : ''}`} error={errors.cep}>
        <input
          type="text"
          inputMode="numeric"
          value={endereco.cep}
          onChange={(e) => handleCepChange(e.target.value)}
          placeholder="00000-000"
          maxLength={9}
          style={inputStyle}
        />
      </Field>

      <Field label="Número" error={errors.numero}>
        <input
          type="text"
          inputMode="numeric"
          value={endereco.numero}
          onChange={(e) => setEndereco({ ...endereco, numero: e.target.value })}
          placeholder="123"
          style={inputStyle}
        />
      </Field>

      {(endereco.rua || endereco.bairro || endereco.cidade) && (
        <div style={{ fontSize: 12, color: 'var(--text-soft)', padding: '0 4px', lineHeight: 1.55, marginBottom: 14 }}>
          {[endereco.rua, endereco.bairro, endereco.cidade && `${endereco.cidade}/${endereco.uf}`]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}

      {serverError && (
        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(214,76,43,0.08)', color: '#9F2A1F', fontSize: 13, marginTop: 8 }}>
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={!formValido || submitting}
        style={{
          width: '100%',
          marginTop: 14,
          padding: '16px 22px',
          borderRadius: 999,
          background: !formValido || submitting ? 'var(--terra)' : 'var(--gold)',
          color: '#fff',
          fontWeight: 700,
          fontSize: 15.5,
          fontFamily: FONT,
          border: 'none',
          cursor: !formValido || submitting ? 'not-allowed' : 'pointer',
          opacity: !formValido || submitting ? 0.55 : 1,
          boxShadow: '0 8px 28px rgba(255,145,77,0.38)',
          letterSpacing: '-0.005em',
        }}
      >
        {submitting ? 'Gerando boleto…' : 'Gerar boleto'}
      </button>

      <style>{`@keyframes inspiraFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </form>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6, letterSpacing: '0.02em' }}>
        {label}
      </label>
      {children}
      {error && <div style={{ color: '#D64C2B', fontSize: 11.5, marginTop: 4, fontWeight: 500 }}>{error}</div>}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '14px 14px',
  borderRadius: 12,
  border: '1px solid var(--border-soft)',
  background: 'var(--surface-white)',
  fontSize: 15.5,
  fontFamily: FONT,
  color: 'var(--text-body)',
  boxSizing: 'border-box',
  outline: 'none',
  WebkitAppearance: 'none' as const,
};
